import React, { useEffect, useState } from "react";
import { PageHero } from "./AboutPage";
import { Lock } from "lucide-react";

// Reveal: Sunday, Oct 25, 2026 at 12:00 PM ET. Set ROSTER_READY true once new E-board is added.
const REVEAL_DATE = new Date("2026-10-25T16:00:00Z");
const ROSTER_READY = false;

// 2025-26 Executive Board
const EBOARD = [
  {
    name: "William McIntyre",
    title: "President",
    photo:
      "https://customer-assets.emergentagent.com/job_gentlemen-collective/artifacts/4140aijy_president.jpg",
  },
  {
    name: "Chris Shorter Jr.",
    title: "Vice President",
    photo:
      "https://customer-assets.emergentagent.com/job_gentlemen-collective/artifacts/cc0adiuf_DSC08211.jpg",
  },
  {
    name: "Mikkahel Toussaint",
    title: "Secretary",
    photo:
      "https://customer-assets.emergentagent.com/job_gentlemen-collective/artifacts/kksvz08s_DSC08224.jpg",
  },
  {
    name: "Silas Murangiri",
    title: "Treasurer",
    photo:
      "https://customer-assets.emergentagent.com/job_gentlemen-collective/artifacts/0w8vxa6a_DSC08250.jpg",
  },
  {
    name: "Bryce Sharpe",
    title: "Historian",
    photo:
      "https://customer-assets.emergentagent.com/job_gentlemen-collective/artifacts/syvrvzjv_DSC08319.jpg",
  },
];

// Executive Chairs
const CHAIRS = [
  {
    name: "Adam Tolliver",
    title: "Branding Chair",
    photo:
      "https://customer-assets.emergentagent.com/job_gentlemen-collective/artifacts/mmuuyimo_DSC08278.jpg",
  },
  {
    name: "Joseph Clayton",
    title: "Communications Chair",
    photo:
      "https://customer-assets.emergentagent.com/job_gentlemen-collective/artifacts/f9ril5h5_DSC08228.jpg",
  },
  {
    name: "Malcolm Weekes",
    title: "Branding Chair",
    photo:
      "https://customer-assets.emergentagent.com/job_gentlemen-collective/artifacts/ovauhu1q_DSC08198.jpg",
  },
  {
    name: "Izayah Hill",
    title: "Social Activities Vice Chair",
    photo:
      "https://customer-assets.emergentagent.com/job_gentlemen-collective/artifacts/9sf35if0_DSC08258.jpg",
  },
  {
    name: "Harold Lynch III",
    title: "Fundraising Chair",
    photo:
      "https://customer-assets.emergentagent.com/job_gentlemen-collective/artifacts/hwovtca4_DSC08287.jpg",
  },
  {
    name: "Bryce Williams",
    title: "Social Activities Chair",
    photo:
      "https://customer-assets.emergentagent.com/job_gentlemen-collective/artifacts/xztm9n8m_DSC08306.jpg",
  },
  {
    name: "Austin King",
    title: "Community Service Chair",
    photo:
      "https://customer-assets.emergentagent.com/job_gentlemen-collective/artifacts/uhfvey0b_DSC08260.jpg",
  },
  {
    name: "Otis Lofton III",
    title: "Sergeant at Arms",
    photo:
      "https://customer-assets.emergentagent.com/job_gentlemen-collective/artifacts/yqza08ka_DSC08209.jpg",
  },
];

const Card = ({ m, i, prefix }) => (
  <div className="bg-white border border-[#D8D3C4] group" data-testid={`${prefix}-${i}`}>
    <div className="aspect-[4/5] overflow-hidden bg-[#EBE8E0]">
      <img
        src={m.photo}
        alt={m.name}
        className="w-full h-full object-cover transition duration-500 group-hover:scale-[1.02]"
      />
    </div>
    <div className="p-6">
      <div className="overline text-[#C9A227]">{m.title}</div>
      <h3 className="font-serif-display text-2xl text-[#0A1F44] mt-1">{m.name}</h3>
    </div>
  </div>
);

const useCountdown = (target) => {
  const compute = () => Math.max(0, target.getTime() - Date.now());
  const [ms, setMs] = useState(compute);
  useEffect(() => {
    const id = setInterval(() => setMs(compute()), 1000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const days = Math.floor(ms / 86400000);
  const hours = Math.floor((ms % 86400000) / 3600000);
  const minutes = Math.floor((ms % 3600000) / 60000);
  const seconds = Math.floor((ms % 60000) / 1000);
  return { ms, days, hours, minutes, seconds, locked: ms > 0 };
};

const CountdownUnit = ({ label, value }) => (
  <div className="text-center border border-[#D4AF37]/40 bg-[#061430] px-4 py-5 md:px-7 md:py-7 min-w-[86px] md:min-w-[120px]" data-testid={`countdown-${label.toLowerCase()}`}>
    <div className="font-serif-display text-5xl md:text-6xl text-[#D4AF37] tabular-nums">
      {String(value).padStart(2, "0")}
    </div>
    <div className="overline text-[#D8D3C4] mt-2">{label}</div>
  </div>
);

const LeadershipLocked = () => {
  const { days, hours, minutes, seconds, locked } = useCountdown(REVEAL_DATE);
  const prettyDate = REVEAL_DATE.toLocaleString("en-US", {
    timeZone: "America/New_York",
    weekday: "long",
    month: "long",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
    timeZoneName: "short",
  });
  return (
    <div data-testid="leadership-locked">
      <PageHero
        overline="Leadership"
        title="The Reveal Is Coming."
        subtitle="The 2026&ndash;2027 Executive Board will be unveiled on the date below. Return then to meet the gentlemen leading GODSC into its next chapter."
      />
      <section className="section-pad">
        <div className="max-w-4xl mx-auto px-5 text-center">
          <div className="inline-flex items-center gap-3 border border-[#D4AF37] text-[#C9A227] px-4 py-2 text-[11px] tracking-[0.28em] uppercase">
            <Lock size={13} /> Page Locked Until Reveal
          </div>

          {locked ? (
            <>
              <div className="mt-10 flex flex-wrap justify-center gap-3 md:gap-4" data-testid="countdown">
                <CountdownUnit label="Days" value={days} />
                <CountdownUnit label="Hours" value={hours} />
                <CountdownUnit label="Minutes" value={minutes} />
                <CountdownUnit label="Seconds" value={seconds} />
              </div>
              <div className="mt-12 overline text-[#C9A227]">Reveal Scheduled For</div>
              <div className="font-serif-display text-2xl md:text-3xl text-[#0A1F44] mt-3" data-testid="reveal-date">
                {prettyDate}
              </div>
            </>
          ) : (
            <div className="mt-12 font-serif-display text-4xl md:text-5xl text-[#0A1F44]" data-testid="leadership-coming-soon">
              Coming Soon.
            </div>
          )}
          <div className="mt-10 h-[2px] w-16 bg-[#D4AF37] mx-auto" />
          <p className="mt-8 text-[#5C5C5C] text-sm leading-relaxed max-w-xl mx-auto">
            Follow <a href="https://instagram.com/godscinc" target="_blank" rel="noreferrer" className="text-[#0A1F44] underline hover:text-[#C9A227]">@godscinc</a>{" "}
            for the official announcement when the reveal goes live.
          </p>
        </div>
      </section>
    </div>
  );
};

const LeadershipRevealed = () => (
  <div>
    <PageHero
      overline="Leadership"
      title="The Executive Board."
      subtitle="The men entrusted with stewarding GODSC through this chapter of its history."
    />

    <section className="section-pad">
      <div className="max-w-7xl mx-auto px-5">
        <div className="overline text-[#C9A227] mb-6">2026&ndash;2027 Executive Board</div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
          {EBOARD.map((m, i) => <Card key={m.title + m.name} m={m} i={i} prefix="eboard" />)}
        </div>

        <div className="mt-24 overline text-[#C9A227] mb-6">Executive Chairs</div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
          {CHAIRS.map((m, i) => <Card key={m.title + m.name} m={m} i={i} prefix="chair" />)}
        </div>
      </div>
    </section>
  </div>
);

const LeadershipPage = () => {
  const { locked } = useCountdown(REVEAL_DATE);
  return locked || !ROSTER_READY ? <LeadershipLocked /> : <LeadershipRevealed />;
};

export default LeadershipPage;
